import type { User } from '../types'
import type { MessageKey } from '../i18n'

/**
 * Structure du menu latéral.
 *
 * `permission` reprend les clés d'onglets déjà stockées dans `allowedTabs` :
 * les droits définis depuis « Utilisateurs » s'appliquent sans migration.
 *
 * Les libellés sont des clés de catalogue, pas du texte : le menu doit suivre
 * la langue choisie, et une entrée ajoutée sans traduction ne compile pas.
 */

export type IconName =
  | 'overview'
  | 'merchants'
  | 'shield'
  | 'globe'
  | 'ads'
  | 'betting'
  | 'search'
  | 'rules'
  | 'users'
  | 'orgs'
  | 'guide'

export type BadgeKey = 'ads.advertisers' | 'betting.tests' | 'betting.qualify'

export interface NavSub {
  path: string
  labelKey: MessageKey
  badge?: BadgeKey
}

export interface NavItem {
  path: string
  labelKey: MessageKey
  icon: IconName
  /** Clé de module exigée. Absente, l'écran est ouvert à tous : voir `alwaysVisible`. */
  permission?: string
  /**
   * Écran d'aide, visible quels que soient les modules souscrits.
   *
   * L'aide ne se vend pas : un utilisateur qui ne voit pas le guide ne peut pas
   * comprendre pourquoi il ne voit pas le reste.
   */
  alwaysVisible?: boolean
  /** Réservé aux administrateurs d'organisation (ou au super admin pour `superadmin`). */
  restrictedTo?: 'manager' | 'superadmin'
  subs?: NavSub[]
}

export interface NavSection {
  titleKey: MessageKey | null
  items: NavItem[]
}

export const NAVIGATION: NavSection[] = [
  {
    titleKey: null,
    items: [{ path: 'overview', labelKey: 'nav.overview', icon: 'overview', permission: 'merchants' }],
  },
  {
    titleKey: 'nav.section.monitoring',
    items: [
      {
        path: 'merchants',
        labelKey: 'nav.merchants',
        icon: 'merchants',
        permission: 'merchants',
        subs: [
          { path: 'merchants', labelKey: 'nav.merchants.portfolio' },
          { path: 'merchants/reports', labelKey: 'nav.merchants.reports' },
        ],
      },
      { path: 'discovery', labelKey: 'nav.discovery', icon: 'shield', permission: 'discovery' },
      { path: 'webscan', labelKey: 'nav.webscan', icon: 'globe', permission: 'webscan' },
    ],
  },
  {
    titleKey: 'nav.section.advertising',
    items: [
      {
        path: 'ads',
        labelKey: 'nav.ads',
        icon: 'ads',
        permission: 'ads',
        subs: [
          { path: 'ads', labelKey: 'nav.ads.overview' },
          { path: 'ads/advertisers', labelKey: 'nav.ads.advertisers', badge: 'ads.advertisers' },
          { path: 'ads/banners', labelKey: 'nav.ads.banners' },
          { path: 'ads/campaigns', labelKey: 'nav.ads.campaigns' },
          { path: 'ads/queries', labelKey: 'nav.ads.queries' },
        ],
      },
    ],
  },
  {
    titleKey: 'nav.section.betting',
    items: [
      {
        path: 'betting',
        labelKey: 'nav.betting',
        icon: 'betting',
        permission: 'betting',
        subs: [
          { path: 'betting', labelKey: 'nav.betting.dashboard' },
          { path: 'betting/tests', labelKey: 'nav.betting.tests', badge: 'betting.tests' },
          { path: 'betting/services', labelKey: 'nav.betting.services' },
          { path: 'betting/discovery', labelKey: 'nav.betting.discovery' },
          { path: 'betting/qualify', labelKey: 'nav.betting.qualify', badge: 'betting.qualify' },
          { path: 'betting/referentials', labelKey: 'nav.betting.referentials' },
        ],
      },
    ],
  },
  {
    titleKey: 'nav.section.configuration',
    items: [
      { path: 'brands', labelKey: 'nav.brands', icon: 'search', permission: 'brands' },
      { path: 'rules', labelKey: 'nav.rules', icon: 'rules', permission: 'rules' },
      { path: 'users', labelKey: 'nav.users', icon: 'users', permission: 'users', restrictedTo: 'manager' },
      { path: 'orgs', labelKey: 'nav.orgs', icon: 'orgs', permission: 'orgs', restrictedTo: 'superadmin' },
    ],
  },
  {
    titleKey: 'nav.section.help',
    items: [{ path: 'guide', labelKey: 'nav.guide', icon: 'guide', alwaysVisible: true }],
  },
]

export function isManagerRole(user: User | null): boolean {
  return user?.role === 'SUPERADMIN' || user?.role === 'OWNER' || user?.role === 'ADMIN'
}

/** Même règle qu'avant la refonte : liste vide = tout est ouvert. */
export function canAccess(user: User | null, item: NavItem): boolean {
  if (!user) return false
  if (item.alwaysVisible) return true
  if (item.restrictedTo === 'superadmin') return user.role === 'SUPERADMIN'
  if (item.restrictedTo === 'manager') return isManagerRole(user)
  if (isManagerRole(user)) return true
  if (!item.permission) return true
  const allowed = user.allowedTabs ?? []
  return allowed.length === 0 || allowed.includes(item.permission)
}

export function visibleNavigation(user: User | null): NavSection[] {
  return NAVIGATION.map((s) => ({ ...s, items: s.items.filter((i) => canAccess(user, i)) })).filter(
    (s) => s.items.length > 0,
  )
}

/** Élément du menu qui porte la route courante. */
export function findItem(path: string[]): NavItem | null {
  const head = path[0] ?? 'overview'
  for (const s of NAVIGATION) for (const i of s.items) if (i.path === head) return i
  return null
}

/** Sous-entrée la plus précise qui correspond à la route courante. */
export function findSub(item: NavItem, path: string[]): NavSub | null {
  if (!item.subs) return null
  const joined = path.join('/')
  let best: NavSub | null = null
  for (const sub of item.subs) {
    if (joined === sub.path || joined.startsWith(`${sub.path}/`)) {
      if (!best || sub.path.length > best.path.length) best = sub
    }
  }
  return best
}
